/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { parse, stringify } from 'query-string';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import values from 'lodash/values';
import orderBy from 'lodash/orderBy';
import { mapSortStringToObject, mapSortObjectToString } from '@Pages/RetroSynthesis/utils';
import { requestParams as requestParamsSelector } from '@Pages/RetroSynthesis/ProjectRoute/selectors';

export function shouldFetchData(callback, newRequestParams) {
  const lastRequestParams = useSelector(requestParamsSelector);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isEmpty(lastRequestParams) || !isEqual(newRequestParams, lastRequestParams)) {
      return callback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, values(newRequestParams));
}

export { default as usePrevious } from '@Hooks/usePrevious';

const _getDefaultSortingProperty =
  ({ property }) =>
  (item) =>
    item[property] ? parseFloat(item[property]) : -1;
const _getDefaultSortingDirection = ({ direction }) => direction.toLowerCase();

const _getSortedDataList = (dataList, sortList, sortingPropertiesMapper, sortingDirectionsMapper) =>
  useMemo(() => {
    try {
      const sortProperties = sortList.reduce((acc, { property }) => {
        const sorters = sortingPropertiesMapper[property] || [_getDefaultSortingProperty({ property })];
        acc.push(...sorters);
        return acc;
      }, []);
      const sortDirections = sortList.reduce((acc, { property, direction }) => {
        const directions = sortingDirectionsMapper[property]
          ? sortingDirectionsMapper[property](direction)
          : [_getDefaultSortingDirection({ direction })];
        acc.push(...directions);
        return acc;
      }, []);
      return orderBy(dataList, sortProperties, sortDirections);
    } catch (e) {
      Logger.warn('Invalid sorting', e);
      return dataList;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList, sortList]);

export const useSortingList = (dataList, defaultSort, { sortingPropertiesMapper = {}, sortingDirectionsMapper = {} } = {}) => {
  const [sortList, setSortList] = useState([defaultSort]);
  return [_getSortedDataList(dataList, sortList, sortingPropertiesMapper, sortingDirectionsMapper), sortList, setSortList];
};

export const useSortingListUrl = (
  dataList,
  defaultSort,
  { urlParam = 'sort', sortingPropertiesMapper = {}, sortingDirectionsMapper = {} } = {}
) => {
  const history = useHistory();
  const location = useLocation();
  const [sortList, setSortList] = useState([defaultSort]);

  useEffect(() => {
    const urlSortStr = parse(location && location.search)[urlParam];
    const urlSortList = [mapSortStringToObject(urlSortStr)];

    if (urlSortStr && !isEqual(urlSortList, sortList)) {
      setSortList(urlSortList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return [
    _getSortedDataList(dataList, sortList, sortingPropertiesMapper, sortingDirectionsMapper),
    sortList,
    (newSortList) => {
      const search = parse(location && location.search);

      history.replace({
        ...location,
        search: stringify(
          {
            ...search,
            [urlParam]: newSortList.map(mapSortObjectToString),
          },
          { encode: false }
        ),
      });
    },
  ];
};
